import React, { useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Paper,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Update as UpdateIcon
} from '@mui/icons-material';
import { useFormContext } from '../context/FormContext';
import { StepProps, Deponent } from '../types';
import FileUploadField from './FileUploadField';

const ReporterPayInfo: React.FC<StepProps> = ({ onNext }) => {
  const {
    formData,
    updateFormData,
    validationErrors,
    validateStep
  } = useFormContext();
  
  // Helper functions for time calculations
  // Convert time strings to minutes for calculations
  const timeStringToMinutes = (timeString?: string): number => {
    if (!timeString) return -1;
    
    // Format: "HH:MM" in 24-hour time
    const [hour, minute] = timeString.split(':');
    return parseInt(hour, 10) * 60 + parseInt(minute, 10);
  };
  
  // Legacy conversion for UI fields if needed
  const convertTo24Hour = (hour: string, minute: string, ampm: string): number => {
    let hourNum = parseInt(hour, 10);
    if (ampm === 'PM' && hourNum !== 12) hourNum += 12;
    if (ampm === 'AM' && hourNum === 12) hourNum = 0;
    return hourNum * 60 + parseInt(minute, 10); // Return time in minutes
  };
  
  const convertJobStartTimeToMinutes = (startTime?: string): number => {
    if (!startTime) return 0;
    
    // Format: "HH:MM:SS" in 24-hour time
    const [hour, minute] = startTime.split(':');
    return parseInt(hour, 10) * 60 + parseInt(minute, 10);
  };
  
  const convertDeponentTime = (deponent: Deponent): { start: number, end: number, duration: number } => {
    // Prefer using the 24-hour format strings if available
    let startMinutes = timeStringToMinutes(deponent.startTime);
    let endMinutes = timeStringToMinutes(deponent.endTime);
    
    // Fall back to the UI fields if 24-hour strings aren't available
    if (startMinutes === -1 && deponent.startTimeHour && deponent.startTimeMinute && deponent.startTimeAmPm) {
      startMinutes = convertTo24Hour(deponent.startTimeHour, deponent.startTimeMinute, deponent.startTimeAmPm);
    }
    
    if (endMinutes === -1 && deponent.endTimeHour && deponent.endTimeMinute && deponent.endTimeAmPm) {
      endMinutes = convertTo24Hour(deponent.endTimeHour, deponent.endTimeMinute, deponent.endTimeAmPm);
    }
    
    // Calculate duration (if both start and end are valid)
    const duration = (startMinutes >= 0 && endMinutes >= 0) ? (endMinutes - startMinutes) : 0;
    
    return { start: startMinutes, end: endMinutes, duration: Math.max(0, duration) };
  };
  
  // Calculate waiting and writing time based on job and deponent times
  const calculateReporterTimes = (): { waitingTime: number, writingTime: number } => {
    let waitingTime = 0;
    let writingTime = 0;
    
    const jobStartTimeMinutes = convertJobStartTimeToMinutes(formData.jobStartTime);
    const validDeponents = formData.deponents.filter(d => 
      d.startTimeHour && d.startTimeMinute && d.startTimeAmPm &&
      d.endTimeHour && d.endTimeMinute && d.endTimeAmPm
    );
    
    if (validDeponents.length === 0) {
      return { waitingTime: 0, writingTime: 0 };
    }
    
    // Sort deponents by start time
    const sortedDeponents = [...validDeponents].sort((a, b) => {
      const aTime = convertDeponentTime(a).start;
      const bTime = convertDeponentTime(b).start;
      return aTime - bTime;
    });
    
    // Calculate writing time (sum of all deposition durations)
    writingTime = sortedDeponents.reduce((total, deponent) => {
      const { duration } = convertDeponentTime(deponent);
      return total + duration;
    }, 0);
    
    // Calculate waiting time
    if (jobStartTimeMinutes > 0) {
      // First, calculate initial waiting (between scheduled job start and first deponent)
      const firstDeponentStart = convertDeponentTime(sortedDeponents[0]).start;
      if (firstDeponentStart > jobStartTimeMinutes) {
        waitingTime += (firstDeponentStart - jobStartTimeMinutes);
      }
    }
    
    // Add gaps between deponents
    for (let i = 0; i < sortedDeponents.length - 1; i++) {
      const currentEnd = convertDeponentTime(sortedDeponents[i]).end;
      const nextStart = convertDeponentTime(sortedDeponents[i + 1]).start;
      
      if (nextStart > currentEnd) {
        waitingTime += (nextStart - currentEnd);
      }
    }
    
    // Convert from minutes to hours (rounded to nearest 0.5)
    return {
      waitingTime: Math.round((waitingTime / 60) * 2) / 2,
      writingTime: Math.round((writingTime / 60) * 2) / 2
    };
  };
  
  // Auto-calculate waiting and writing time only when values have never been set (undefined).
  // Once a value is set (by auto-calc or user), it won't be overwritten — even if the user
  // clears it (null). The explicit Recalculate button is available for re-triggering.
  useEffect(() => {
    if (formData.deponents.length > 0) {
      const { waitingTime, writingTime } = calculateReporterTimes();

      if (formData.waitingTime === undefined) {
        updateFormData('waitingTime', waitingTime);
      }

      if (formData.writingTime === undefined) {
        updateFormData('writingTime', writingTime);
      }
    }
  // ESLINT NOTE: Intentionally omitting calculateReporterTimes (inline function, new ref every render),
  // formData.waitingTime/writingTime (adding them would re-run the effect after each auto-fill,
  // defeating the "only set when undefined" intent), and updateFormData (stable context ref).
  // This effect should only fire when the source data (deponents, jobStartTime) changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.deponents, formData.jobStartTime]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (e.target.type === 'number') {
      updateFormData(name as keyof typeof formData, value === '' ? null : parseFloat(value));
    } else {
      updateFormData(name as keyof typeof formData, value);
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    updateFormData(name as keyof typeof formData, value);
  };

  const handleSubmit = () => {
    const isValid = validateStep('pay-info');
    if (isValid && onNext) {
      onNext();
    }
  };

  const attendanceTypes = [
    'No Appearance Fee',
    'Full Day',
    'Half Day',
    'Hourly',
    'Non-Transcribe',
    'Standard Attendance (Flat Fee)',
    'Evening',
    'Weekend'
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h2" component="h2" gutterBottom>
          Reporter Pay Information
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3 }}>
          Please provide a detailed breakdown of your payroll for this job.
          The information will assist our Billing Department in generating accurate payroll.
        </Typography>
        
        {/* Display reporter name and email if available */}
        {(formData.reporterName || formData.reporterEmail) && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reporter Name"
                  value={formData.reporterName}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Reporter Email"
                  value={formData.reporterEmail}
                  InputProps={{
                    readOnly: true,
                  }}
                  variant="outlined"
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl 
              fullWidth 
              error={!!validationErrors.attendanceType}
            >
              <InputLabel id="attendance-type-label">Attendance Type *</InputLabel>
              <Select
                labelId="attendance-type-label"
                id="attendanceType"
                name="attendanceType"
                value={formData.attendanceType}
                onChange={handleSelectChange}
                label="Attendance Type *"
              >
                <MenuItem value="">Select Attendance Type</MenuItem>
                {attendanceTypes.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
              {validationErrors.attendanceType && (
                <Typography variant="caption" color="error">
                  {validationErrors.attendanceType}
                </Typography>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              id="waitingTime"
              name="waitingTime"
              label="Waiting Time (Hours)"
              value={formData.waitingTime ?? ''}
              onChange={handleTextChange}
              onWheel={(e) => (e.target as HTMLElement).blur()}
              inputProps={{
                min: 0,
                max: 10,
                step: 0.5
              }}
              placeholder="0"
              helperText="Auto-calculated based on job and deponent times"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Chip 
                      label="Auto" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              id="writingTime"
              name="writingTime"
              label="Writing Time (Hours)"
              value={formData.writingTime ?? ''}
              onChange={handleTextChange}
              onWheel={(e) => (e.target as HTMLElement).blur()}
              inputProps={{
                min: 0,
                max: 20,
                step: 0.5
              }}
              placeholder="0"
              helperText="Auto-calculated from deposition durations"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Chip 
                      label="Auto" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ mr: 1 }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<UpdateIcon />}
              onClick={() => {
                const { waitingTime, writingTime } = calculateReporterTimes();
                updateFormData('waitingTime', waitingTime);
                updateFormData('writingTime', writingTime);
              }}
              size="small"
              sx={{ mt: -2, mb: 2 }}
            >
              Recalculate from deponent times
            </Button>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              id="attendanceFee"
              name="attendanceFee"
              label="Attendance Fee"
              value={formData.attendanceFee}
              onChange={handleTextChange}
              placeholder="0.00"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>

        </Grid>

        <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
          <Typography variant="h3" component="h3" gutterBottom>
            Payroll Details
          </Typography>
          
          <TextField
            fullWidth
            id="payrollInfo"
            name="payrollInfo"
            label="Payroll Information"
            multiline
            rows={6}
            value={formData.payrollInfo}
            onChange={handleTextChange}
            error={!!validationErrors.payrollInfo}
            helperText={validationErrors.payrollInfo || "Enter detailed breakdown of your pay or attach your invoice below"}
            placeholder="Enter detailed breakdown of your pay or attach your invoice using the file upload below. Missing or incomplete information may result in delayed payment."
            required
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 3 }}>
            <FileUploadField
              title="Invoice Upload (Optional)"
              type="invoiceFile"
              required={false}
            />
          </Box>

          <TextField
            fullWidth
            id="specialNotes"
            name="specialNotes"
            label="Special Payroll & Billing Notes"
            multiline
            rows={4}
            value={formData.specialNotes}
            onChange={handleTextChange}
            placeholder="Enter any special notes related to billing or payroll for this job"
          />
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
          >
            Review Submission
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReporterPayInfo;